import { FC } from 'react';
import type { Battery } from '@/hooks/useBatteryContainers';

interface BatteryStatusSummaryProps {
  batteries: Battery[];
  allocations?: Map<string, string>;
}

interface StatusGroup {
  powerKw: number;
  customerName: string;
  count: number;
}

export const BatteryStatusSummary: FC<BatteryStatusSummaryProps> = ({
  batteries,
  allocations,
}) => {
  const groupByStatus = (status: string) => {
    const filtered = batteries.filter(b => b.status === status);
    const groups = new Map<string, StatusGroup>();

    filtered.forEach(battery => {
      const customerName = allocations?.get(battery.id) || 'Niet toegewezen';
      const key = `${battery.power_kw}-${customerName}`;
      
      if (groups.has(key)) {
        groups.get(key)!.count++;
      } else {
        groups.set(key, {
          powerKw: battery.power_kw,
          customerName,
          count: 1,
        });
      }
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.powerKw !== b.powerKw) return a.powerKw - b.powerKw;
      return a.customerName.localeCompare(b.customerName);
    });
  };

  const vrijGroups = groupByStatus('vrij');
  const gereserveerdGroups = groupByStatus('gereserveerd');
  const ingeplandGroups = groupByStatus('ingepland');

  const StatusSection = ({ title, groups, colorClass }: { title: string; groups: StatusGroup[]; colorClass: string }) => (
    <div className="space-y-1">
      <h4 className={`text-xs font-semibold ${colorClass}`}>{title}</h4>
      {groups.length === 0 ? (
        <p className="text-xs text-muted-foreground">Geen batterijen</p>
      ) : (
        <div className="space-y-0.5">
          {groups.map((group, idx) => (
            <div key={idx} className="text-xs flex justify-between">
              <span className="text-muted-foreground">
                {group.powerKw}kW - {group.customerName}
              </span>
              <span className="font-medium">{group.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3 pt-3 border-t">
      <StatusSection title="Vrij" groups={vrijGroups} colorClass="text-green-600 dark:text-green-400" />
      <StatusSection title="Gereserveerd" groups={gereserveerdGroups} colorClass="text-yellow-600 dark:text-yellow-400" />
      <StatusSection title="Ingepland" groups={ingeplandGroups} colorClass="text-red-600 dark:text-red-400" />
    </div>
  );
};
